/**
 * Message formatting shared with AiChatPanel / headless controller.
 */

/**
 * 规范化「思考过程」类纯文本：去前缀、合并异常空白，与面板展示逻辑一致。
 * @param {string|null|undefined} text
 * @returns {string}
 */
export function normalizeReasoningPlain (text) {
  if (text == null || text === '') return ''
  let s = String(text).replace(/\r\n/g, '\n')
  s = s.replace(/^\[思考[\s\u3000\u00a0]*过程\][\s\u3000\u00a0]*/m, '')
  s = s.replace(/(^|\n)[\s\u3000\u00a0]*思考[\s\u3000\u00a0]+过程[\s\u3000\u00a0]*(?=\n|$)/g, '\n')
  s = s.replace(/思考[\s\u3000\u00a0]+过程/g, '思考过程')
  s = s.replace(/\n{3,}/g, '\n\n')
  return s.trim()
}

/**
 * 将可能含 HTML 的字符串转为纯文本（浏览器内用 DOM；无 document 时简单去标签）。
 * @param {string|null|undefined} html
 * @returns {string}
 */
export function messageHtmlToPlain (html) {
  if (!html) return ''
  if (typeof document === 'undefined') return String(html).replace(/<[^>]+>/g, '')
  const div = document.createElement('div')
  div.innerHTML = html
  return (div.textContent || '').trim()
}

function cloneSerializable (value, fallback) {
  try {
    return JSON.parse(JSON.stringify(value))
  } catch (e) {
    return fallback
  }
}

export function normalizeProcessPart (part) {
  const raw = cloneSerializable(part || {}, {})
  const state = raw && raw.state && typeof raw.state === 'object'
    ? cloneSerializable(raw.state, null)
    : null
  return {
    id: raw.id || '',
    type: raw.type || 'unknown',
    tool: raw.tool || '',
    callID: raw.callID || '',
    text: raw.text != null ? String(raw.text) : '',
    title: (state && state.title) || raw.tool || raw.type || 'unknown',
    status: (state && state.status) || '',
    state,
    raw,
    sourceSessionId: raw.sourceSessionId || '',
    sourceTitle: raw.sourceTitle || ''
  }
}

/**
 * 将接口返回的连续多条 assistant 气泡合并为一条，思考多段拼接、正文取组内最后一条有内容的。
 * @param {Array<{ role: string, content?: string, reasoning?: string, processParts?: Array, messageId?: string }>} list
 * @returns {typeof list}
 */
export function mergeConsecutiveAssistantBubbles (list) {
  if (!list || list.length === 0) {
    return list
  }
  const out = []
  let i = 0
  while (i < list.length) {
    const m = list[i]
    if (m.role !== 'assistant') {
      out.push(m)
      i++
      continue
    }
    const group = []
    while (i < list.length && list[i].role === 'assistant') {
      group.push(list[i])
      i++
    }
    if (group.length === 1) {
      out.push(group[0])
      continue
    }
    const reasonPlainParts = group
      .map((g) => normalizeReasoningPlain(messageHtmlToPlain(g.reasoning)))
      .filter(Boolean)
    const mergedReasoning = reasonPlainParts.length
      ? normalizeReasoningPlain(reasonPlainParts.join('\n\n'))
      : ''
    const mergedProcessParts = []
    const mergedDisplayParts = []
    for (const item of group) {
      if (Array.isArray(item.processParts) && item.processParts.length) {
        mergedProcessParts.push(...item.processParts.map(part => normalizeProcessPart(part)))
      }
      if (Array.isArray(item.displayParts) && item.displayParts.length) {
        mergedDisplayParts.push(...item.displayParts.map(displayPart => {
          if (displayPart && displayPart.type === 'process') {
            return {
              type: 'process',
              part: normalizeProcessPart(displayPart.part)
            }
          }
          return {
            type: 'text',
            text: displayPart && displayPart.text != null ? String(displayPart.text) : ''
          }
        }))
      }
    }
    let mergedContent = ''
    for (let k = group.length - 1; k >= 0; k--) {
      if (messageHtmlToPlain(group[k].content)) {
        mergedContent = group[k].content
        break
      }
    }
    const last = group[group.length - 1]
    out.push(
      Object.assign({}, last, {
        reasoning: mergedReasoning,
        content: mergedContent || '',
        processParts: mergedProcessParts,
        displayParts: mergedDisplayParts,
        messageId: last.messageId
      })
    )
  }
  return out
}

/**
 * 将 OpenCode session.messages 接口的原始数组转为 UI 用的气泡列表（含合并连续 assistant）。
 * @param {Array} messagesData
 * @returns {Array<{ role: string, content: string, reasoning: string, processParts: Array, timestamp: *, messageId: * }>}
 */
export function formatMessages (messagesData) {
  if (!Array.isArray(messagesData) || messagesData.length === 0) {
    return []
  }

  const formattedMessages = []

  for (const msg of messagesData) {
    if (!msg.parts || !Array.isArray(msg.parts)) {
      continue
    }

    let content = ''
    const reasoningPartTexts = []
    const processParts = []
    const displayParts = []

    for (const part of msg.parts) {
      if (part.type === 'text') {
        const text = part.text || ''
        content += text
        if (text) {
          displayParts.push({ type: 'text', text })
        }
      } else if (part.type === 'reasoning') {
        reasoningPartTexts.push(part.text != null ? String(part.text) : '')
      } else {
        const normalizedPart = normalizeProcessPart(part)
        processParts.push(normalizedPart)
        displayParts.push({ type: 'process', part: normalizedPart })
      }
    }

    const reasoning = reasoningPartTexts.filter((t) => t && t.trim()).join('\n\n')
    const cTrim = content.trim()
    const rNorm = normalizeReasoningPlain(reasoning)
    const rTrim = rNorm.trim()
    if (cTrim || rTrim || processParts.length) {
      formattedMessages.push({
        role: msg.info.role,
        content: cTrim || '',
        reasoning: (msg.info.role === 'assistant' && rTrim) ? rTrim : '',
        processParts,
        displayParts,
        timestamp: msg.info.time && msg.info.time.created ? msg.info.time.created : null,
        messageId: msg.info.id
      })
    }
  }

  return mergeConsecutiveAssistantBubbles(formattedMessages)
}
